if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          dots: this.querySelector('.media-gallery-dots'),   // ← add this
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.initDragToScroll();
        if (!this.elements.thumbnails) return;

        // Short debounce so the thumbnail strip tracks the main image while dragging
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 100));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        if (this.elements.dots) {
          this.elements.dots.querySelectorAll('.media-gallery-dot').forEach((dot) => {
            dot.addEventListener('click', this.setActiveMedia.bind(this, dot.dataset.target, false));
          });
        }
      }

      /*
       * Mouse/pen drag-to-scroll for the main media slider. Touch devices already
       * scroll natively, but an overflow container is not draggable with a mouse,
       * so we translate pointer movement into scrollLeft and snap to the nearest
       * slide on release. Scrolling fires `slideChanged`, which moves the
       * thumbnail strip to the newly active image.
       */
      initDragToScroll() {
        const slider = this.elements.viewer?.querySelector('[id^="Slider-"]');
        if (!slider) return;

        let pointerId = null;
        let startX = 0;
        let startScrollLeft = 0;
        let isDragging = false;

        const visibleSlides = () =>
          Array.from(slider.querySelectorAll('[id^="Slide-"]')).filter((slide) => slide.clientWidth > 0);

        const endDrag = () => {
          if (pointerId === null) return;

          const wasDragging = isDragging;
          if (slider.hasPointerCapture?.(pointerId)) slider.releasePointerCapture(pointerId);
          pointerId = null;
          isDragging = false;
          slider.classList.remove('is-dragging');
          slider.style.scrollBehavior = '';

          if (wasDragging) {
            // Snap to the closest slide, then hand scroll-snap back to the browser
            const slides = visibleSlides();
            const closest = slides.reduce(
              (nearest, slide) =>
                Math.abs(slide.offsetLeft - slider.scrollLeft) < Math.abs(nearest.offsetLeft - slider.scrollLeft)
                  ? slide
                  : nearest,
              slides[0]
            );
            if (closest) slider.scrollTo({ left: closest.offsetLeft, behavior: 'smooth' });
            slider.style.scrollSnapType = '';

            // Swallow the click that follows a drag so the media modal doesn't open
            slider.addEventListener('click', (event) => event.stopPropagation(), { capture: true, once: true });
          } else {
            slider.style.scrollSnapType = '';
          }
        };

        slider.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'touch' || event.button !== 0) return;
          if (slider.scrollWidth <= slider.clientWidth) return;
          pointerId = event.pointerId;
          startX = event.clientX;
          startScrollLeft = slider.scrollLeft;
          isDragging = false;
        });

        slider.addEventListener('pointermove', (event) => {
          if (pointerId === null || event.pointerId !== pointerId) return;

          const delta = event.clientX - startX;
          if (!isDragging) {
            if (Math.abs(delta) < 5) return;
            isDragging = true;
            slider.classList.add('is-dragging');
            slider.style.scrollSnapType = 'none';
            slider.style.scrollBehavior = 'auto';
            slider.setPointerCapture?.(pointerId);
          }

          event.preventDefault();
          slider.scrollLeft = startScrollLeft - delta;
        });

        slider.addEventListener('pointerup', endDrag);
        slider.addEventListener('pointercancel', endDrag);
        slider.addEventListener('lostpointercapture', endDrag);
        slider.addEventListener('dragstart', (event) => event.preventDefault());
      }

      onSlideChanged(event) {
        const currentElement = event.detail.currentElement;
        if (!currentElement) return;

        const mediaId = currentElement.dataset.mediaId;

        // Keep `is-active` on the slide the user has scrolled to, so the thumbnail
        // highlight, deferred media (video/model) and modal opener stay in sync.
        this.elements.viewer
          .querySelectorAll('[data-media-id]')
          .forEach((element) => element.classList.toggle('is-active', element === currentElement));

        const thumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(thumbnail);

        const dot = this.elements.dots?.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveDot(dot);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        const activeDot = this.elements.dots?.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveDot(activeDot);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }
      setActiveDot(dot) {
        if (!this.elements.dots || !dot) return;
        this.elements.dots.querySelectorAll('.media-gallery-dot').forEach((d) => {
          d.classList.remove('is-active');
          d.setAttribute('aria-selected', 'false');
        });
        dot.classList.add('is-active');
        dot.setAttribute('aria-selected', 'true');
      }
      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
